/**
 * InviteBanner Component (Phase 2 of the shareable join link, #171).
 *
 * The invite affordance shown on the ecosystem landing page when a recipient
 * arrives via an invite link (`?invite=<token>`). Layout (from the Figma):
 *
 *   [ title + subtitle ] [ Sign up ] [ Explore ]
 *
 * The navy pill and the network/constellation decoration come straight from the
 * Figma SVG export (invite-banner-bg.svg), used as a background layer; the text
 * and buttons are real, dynamic elements layered on top.
 *
 * This is the STATIC design component: it takes its content via props and
 * renders no logic. Resolving the token into the ecosystem/inviter and wiring
 * the buttons to the join flow are separate Phase 2 steps; this component just
 * displays what it's given, so it can be styled/previewed on its own.
 *
 * Styling follows the repo rules: Bootstrap utilities for layout, a companion
 * InviteBanner.css for the custom bits (no inline styles).
 */
import React from 'react';
import PropTypes from 'prop-types';

import bgImage from '@/assets/img/invite/invite-banner-bg.svg';

import './InviteBanner.css';

export default function InviteBanner({
    ecosystemName = 'Colorado STEM Ecosystem',
    inviterName = 'A member',
    primaryLabel = 'Sign up',
    onPrimary = () => {},
    onExplore = () => {},
    onDismiss = null,
    primaryDisabled = false,
}) {
    return (
        <div className="invite-banner d-flex flex-column flex-md-row align-items-center gap-3 gap-md-4 px-4 py-3 px-md-5">
            {/* Navy pill + network decoration, straight from the Figma export.
                Purely decorative, so hidden from assistive tech. */}
            <img
                className="invite-banner__bg"
                src={bgImage}
                alt=""
                aria-hidden="true"
            />

            {/* Invite copy. */}
            <div className="text-center text-md-start flex-grow-1">
                <h3 className="invite-banner__title h4 fw-bold mb-1">
                    You&apos;ve been invited to join this ecosystem!
                </h3>
                <p className="invite-banner__subtitle mb-0">
                    {inviterName} has invited you to join the {ecosystemName}
                </p>
            </div>

            {/* Actions. Orange is not a Bootstrap theme color, so both buttons
                are styled in InviteBanner.css. The primary (filled) action is
                dynamic — "Sign up" when logged out, "Join" when logged in;
                Explore is the secondary (outline) action. */}
            <div className="invite-banner__actions d-flex gap-2 flex-shrink-0">
                <button
                    type="button"
                    className="btn invite-banner__btn invite-banner__btn--signup rounded-pill px-4"
                    onClick={onPrimary}
                    disabled={primaryDisabled}
                >
                    {primaryLabel}
                </button>
                <button
                    type="button"
                    className="btn invite-banner__btn invite-banner__btn--explore rounded-pill px-4"
                    onClick={onExplore}
                >
                    Explore
                </button>
            </div>

            {/* Dismiss (X): hides the banner for the rest of the session. */}
            {onDismiss && (
                <button
                    type="button"
                    className="invite-banner__close"
                    aria-label="Dismiss invitation"
                    onClick={onDismiss}
                >
                    <svg
                        viewBox="0 0 16 16"
                        width="11"
                        height="11"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        aria-hidden="true"
                    >
                        <path d="M3 3 L13 13 M13 3 L3 13" />
                    </svg>
                </button>
            )}
        </div>
    );
}

InviteBanner.propTypes = {
    // The ecosystem the recipient is invited to (display name).
    ecosystemName: PropTypes.string,
    // The inviter's display name in "First L" form (e.g. "Sebastian R"),
    // used in the "X has invited you" line.
    inviterName: PropTypes.string,
    // Primary CTA: label + handler. "Sign up" when logged out, "Join" when
    // logged in, "Go to dashboard" for existing members (set by the container).
    primaryLabel: PropTypes.string,
    onPrimary: PropTypes.func,
    primaryDisabled: PropTypes.bool,
    // Secondary CTA: explore the ecosystem's directory.
    onExplore: PropTypes.func,
    // Dismiss handler; when provided, an X is rendered in the corner.
    onDismiss: PropTypes.func,
};
